/**
 * AMB-200 / CORE-2092 — когда истекает JWT и пора ли его обновлять.
 *
 * Обновление по таймеру (`AuthContext.scheduleRefresh`) ставится на ~23,5 часа
 * вперёд, а `setTimeout` считает время, пока компьютер не спит: ноутбук,
 * закрытый на ночь, сдвигает срабатывание на часы. CORE-2092 (прод, 04.11):
 * ведущая вошла 03.11 в 09:56 UTC, токен истёк 04.11 в 09:56 — посреди эфира,
 * — а таймер так и не сработал. Хаб чата держал уже открытое соединение, и
 * текст уходил, а загрузка вложения шла обычным запросом и получала 401.
 *
 * Поэтому срок проверяется ещё и по часам на стене — перед запросом
 * (`api.getAuthToken`). Таймер остаётся: он обновляет токен и тогда, когда
 * запросов нет.
 */

/** За сколько до истечения обновлять токен. Владелец: тихое обновление. */
export const JWT_REFRESH_LEAD_MS = 30 * 60 * 1000;

/**
 * `exp` токена в миллисекундах. Best-effort: битый токен или токен без `exp`
 * дают `null`, и тогда никто ничего не обновляет по сроку.
 */
export function getJwtExpiryMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // Base64URL → Base64 + padding so atob() accepts it.
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(
      typeof atob === 'function'
        ? atob(padded)
        : Buffer.from(padded, 'base64').toString('utf8')
    );
    const exp = typeof json?.exp === 'number' ? json.exp : null;
    return exp ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Пора ли обновить токен перед запросом: он ещё действует, но истечёт в
 * пределах `leadMs`. Истёкший не обновляем — сервер обновляет только
 * действующий (`POST /api/Auth/refresh` под `[Authorize]`), запрос был бы
 * лишним.
 */
export function jwtNeedsRefresh(token: string, nowMs: number, leadMs = JWT_REFRESH_LEAD_MS): boolean {
  const expMs = getJwtExpiryMs(token);
  if (expMs == null) return false;
  return expMs > nowMs && expMs - nowMs <= leadMs;
}
